import "core-js/modules/es.error.cause.js";
import "core-js/modules/es.array.push.js";
import _ from 'lodash';
import { DoctorService } from '@/services/doctor.service';
export default {
  props: {
    dateRange: {
      type: Object,
      required: true
    },
    category: {
      type: String,
      required: true,
      validator: value => ['today', 'custom'].includes(value)
    }
  },
  data() {
    return {
      originalItems: [],
      items: [],
      search: '',
      debouncedSearch: '',
      fetchingReports: false,
      fetchingUpdate: {},
      showEmptyResultsAlert: false,
      searchDebouncer: null
    };
  },
  computed: {
    endDateReferenceTime() {
      return this.toEndOfDayEpoch(this.dateRange.end);
    },
    tableStyle() {
      return {
        maxHeight: 'calc(100vh - 300px)',
        height: 'calc(100vh - 300px)'
      };
    },
    headers() {
      const isToday = this.category === 'today';
      const baseHeaders = [{
        key: 'photo',
        label: ''
      }, {
        key: 'name',
        label: 'Doctor'
      }, {
        key: 'averagetotaldelinquent',
        label: isToday ? 'Total Delinquent' : 'Ave. Total Delinquent',
        sortable: true
      }, {
        key: 'averagemessages',
        label: isToday ? 'Messages' : 'Ave. Messages',
        sortable: true
      }, {
        key: 'averagefollowups',
        label: isToday ? 'Follow-ups' : 'Ave. Follow-ups',
        sortable: true
      }, {
        key: 'oldestpendingtask',
        label: isToday ? 'Oldest Pending Task' : 'Ave. Oldest Pending Task'
      }];
      if (isToday) {
        baseHeaders.push({
          key: 'actions',
          label: ''
        });
      }
      return baseHeaders;
    }
  },
  watch: {
    search() {
      this.searchDebouncer();
    },
    async debouncedSearch() {
      this.applySearch();
    },
    async category(value) {
      if (value === 'custom') {
        this.items = [];
      } else {
        await this.getDelinquentReports();
      }
    }
  },
  async mounted() {
    this.searchDebouncer = _.debounce(() => {
      this.debouncedSearch = this.search;
    }, 500);
    if (this.category === 'today') {
      await this.getDelinquentReports();
    }
  },
  beforeDestroy() {
    if (this.searchDebouncer) {
      this.searchDebouncer.cancel();
    }
  },
  methods: {
    async getDelinquentReports() {
      this.items = [];
      this.fetchingReports = true;
      try {
        const isToday = this.category === 'today';
        const {
          data
        } = isToday ? await DoctorService.getLatestDelinquentReport() : await DoctorService.getDelinquentReport(null, this.toStartOfDayEpoch(this.dateRange.start), this.toEndOfDayEpoch(this.dateRange.end));
        if (data.length === 0) {
          return;
        }
        const processed = isToday ? this.processTodayReports(data, Date.now() / 1000) : this.processCustomRangeReports(data, this.endDateReferenceTime);
        this.originalItems = processed;
        this.items = processed;
      } catch (error) {
        console.error('Failed to fetch delinquent reports:', error);
        this.$bvToast.toast('Failed to load reports. Please try again.', {
          title: 'Error',
          variant: 'danger',
          toaster: 'b-toaster-top-center',
          solid: true
        });
      } finally {
        this.fetchingReports = false;
      }
    },
    processCustomRangeReports(data, referenceTime) {
      const grouped = _.groupBy(data, 'doctor_id');
      return Object.values(grouped).map(records => this.aggregateDoctorRecords(records, referenceTime)).sort((a, b) => b.averagetotaldelinquent - a.averagetotaldelinquent);
    },
    aggregateDoctorRecords(doctorRecords, referenceTime) {
      var _$minBy, _$minBy2;
      const base = doctorRecords[0];
      const avgPatientMessages = Math.round(_.meanBy(doctorRecords, r => parseFloat(r.average_patient_message_count || 0)));
      const avgAdminMessages = Math.round(_.meanBy(doctorRecords, r => parseFloat(r.average_admin_message_count || 0)));
      const avgFollowups = Math.round(_.meanBy(doctorRecords, r => parseFloat(r.average_followup_count || 0)));
      const avgUpsells = Math.round(_.meanBy(doctorRecords, r => parseFloat(r.average_accepted_upsell_count || 0)));
      const oldestPatientTimestamp = (_$minBy = _.minBy(doctorRecords, r => r.average_patient_oldest_timestamp)) === null || _$minBy === void 0 ? void 0 : _$minBy.average_patient_oldest_timestamp;
      const oldestAdminTimestamp = (_$minBy2 = _.minBy(doctorRecords, r => r.average_admin_oldest_timestamp)) === null || _$minBy2 === void 0 ? void 0 : _$minBy2.average_admin_oldest_timestamp;
      const aggregated = {
        ...base,
        average_patient_message_count: avgPatientMessages,
        average_admin_message_count: avgAdminMessages,
        average_followup_count: avgFollowups,
        average_accepted_upsell_count: avgUpsells,
        average_patient_oldest_timestamp: oldestPatientTimestamp,
        average_admin_oldest_timestamp: oldestAdminTimestamp
      };
      return {
        ...aggregated,
        averagetotaldelinquent: this.calculateTotalDelinquent(aggregated),
        averagemessages: this.calculateTotalMessages(aggregated),
        averagefollowups: avgFollowups,
        oldestpendingtask: this.getOldestPendingTask(aggregated, referenceTime)
      };
    },
    processTodayReports(data, referenceTime) {
      return data.map(record => {
        var _ref, _record$followup_coun;
        return {
          ...record,
          averagetotaldelinquent: this.calculateTotalDelinquent(record),
          averagemessages: this.calculateTotalMessages(record),
          averagefollowups: Math.round(parseFloat((_ref = (_record$followup_coun = record.followup_count) !== null && _record$followup_coun !== void 0 ? _record$followup_coun : record.average_followup_count) !== null && _ref !== void 0 ? _ref : 0)),
          oldestpendingtask: this.getOldestPendingTask(record, referenceTime)
        };
      }).sort((a, b) => b.averagetotaldelinquent - a.averagetotaldelinquent);
    },
    processSingleReport(record, referenceTime) {
      var _ref2, _record$followup_coun2;
      return {
        ...record,
        averagetotaldelinquent: this.calculateTotalDelinquent(record),
        averagemessages: this.calculateTotalMessages(record),
        averagefollowups: Math.round(parseFloat((_ref2 = (_record$followup_coun2 = record.followup_count) !== null && _record$followup_coun2 !== void 0 ? _record$followup_coun2 : record.average_followup_count) !== null && _ref2 !== void 0 ? _ref2 : 0)),
        oldestpendingtask: this.getOldestPendingTask(record, referenceTime)
      };
    },
    clearSearch() {
      this.search = '';
      this.$refs.searchInput.focus();
    },
    calculateTotalDelinquent(item) {
      var _ref3, _item$patient_message, _ref4, _item$followup_count, _ref5, _item$admin_message_c, _ref6, _item$accepted_upsell;
      return Math.round(parseFloat((_ref3 = (_item$patient_message = item.patient_message_count) !== null && _item$patient_message !== void 0 ? _item$patient_message : item.average_patient_message_count) !== null && _ref3 !== void 0 ? _ref3 : 0) + parseFloat((_ref4 = (_item$followup_count = item.followup_count) !== null && _item$followup_count !== void 0 ? _item$followup_count : item.average_followup_count) !== null && _ref4 !== void 0 ? _ref4 : 0) + parseFloat((_ref5 = (_item$admin_message_c = item.admin_message_count) !== null && _item$admin_message_c !== void 0 ? _item$admin_message_c : item.average_admin_message_count) !== null && _ref5 !== void 0 ? _ref5 : 0) + parseFloat((_ref6 = (_item$accepted_upsell = item.accepted_upsell_count) !== null && _item$accepted_upsell !== void 0 ? _item$accepted_upsell : item.average_accepted_upsell_count) !== null && _ref6 !== void 0 ? _ref6 : 0));
    },
    calculateTotalMessages(item) {
      var _ref7, _item$patient_message2, _ref8, _item$admin_message_c2, _ref9, _item$accepted_upsell2;
      return Math.round(parseFloat((_ref7 = (_item$patient_message2 = item.patient_message_count) !== null && _item$patient_message2 !== void 0 ? _item$patient_message2 : item.average_patient_message_count) !== null && _ref7 !== void 0 ? _ref7 : 0) + parseFloat((_ref8 = (_item$admin_message_c2 = item.admin_message_count) !== null && _item$admin_message_c2 !== void 0 ? _item$admin_message_c2 : item.average_admin_message_count) !== null && _ref8 !== void 0 ? _ref8 : 0) + parseFloat((_ref9 = (_item$accepted_upsell2 = item.accepted_upsell_count) !== null && _item$accepted_upsell2 !== void 0 ? _item$accepted_upsell2 : item.average_accepted_upsell_count) !== null && _ref9 !== void 0 ? _ref9 : 0));
    },
    getOldestPendingTask(item, referenceTime) {
      var _item$patient_oldest_, _item$admin_oldest_ti, _item$accepted_upsell3, _item$followup_oldest;
      const patientTimestamp = (_item$patient_oldest_ = item.patient_oldest_timestamp) !== null && _item$patient_oldest_ !== void 0 ? _item$patient_oldest_ : item.average_patient_oldest_timestamp;
      const adminTimestamp = (_item$admin_oldest_ti = item.admin_oldest_timestamp) !== null && _item$admin_oldest_ti !== void 0 ? _item$admin_oldest_ti : item.average_admin_oldest_timestamp;
      const upsellTimestamp = (_item$accepted_upsell3 = item.accepted_upsell_oldest_timestamp) !== null && _item$accepted_upsell3 !== void 0 ? _item$accepted_upsell3 : item.average_accepted_upsell_oldest_timestamp;
      const followupTimestamp = (_item$followup_oldest = item.followup_oldest_timestamp) !== null && _item$followup_oldest !== void 0 ? _item$followup_oldest : item.average_followup_oldest_timestamp;
      const timestamps = [patientTimestamp, adminTimestamp, upsellTimestamp, followupTimestamp].filter(timestamp => timestamp !== undefined && timestamp !== null);
      if (timestamps.length === 0) {
        return '-';
      }
      const oldestTimestamp = Math.min(...timestamps);
      const elapsedInSeconds = (referenceTime !== null && referenceTime !== void 0 ? referenceTime : Date.now() / 1000) - oldestTimestamp;
      const elapsedInHours = Math.ceil(elapsedInSeconds / 3600);
      return `${elapsedInHours} hours`;
    },
    toStartOfDayEpoch(date) {
      const d = new Date(date);
      return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0) / 1000;
    },
    toEndOfDayEpoch(date) {
      const d = new Date(date);
      return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59) / 1000;
    },
    applySearch() {
      const search = this.debouncedSearch.trim().toLowerCase();
      if (!search) {
        this.items = this.originalItems;
        this.showEmptyResultsAlert = false;
        return;
      }
      const filtered = this.originalItems.filter(item => {
        const fullName = `${item.firstname} ${item.lastname}`.toLowerCase();
        return fullName.includes(search);
      });
      this.items = filtered;
      this.showEmptyResultsAlert = filtered.length === 0;
    },
    async refreshReport(reportId) {
      if (this.category !== 'today') {
        return;
      }
      this.$set(this.fetchingUpdate, reportId, true);
      try {
        const {
          status,
          data
        } = await DoctorService.refreshDelinquentReport(reportId);
        if (status !== 200) {
          throw new Error('Failed to refresh report');
        }
        const report = Array.isArray(data) ? data[0] : data;
        if (!report || typeof report !== 'object') {
          this.$bvToast.toast('Report generation is already in progress. Please try again shortly.', {
            title: 'Info',
            variant: 'info',
            toaster: 'b-toaster-top-center',
            solid: true
          });
          return;
        }
        const isRemovedFromDelinquent = Object.keys(report).length === 0 || this.calculateTotalDelinquent(report) === 0;
        if (isRemovedFromDelinquent) {
          this.items = this.items.filter(item => item.report_id !== reportId);
          this.originalItems = this.originalItems.filter(item => item.report_id !== reportId);
          this.$bvToast.toast('Report refreshed successfully! Removed from list.', {
            title: 'Success',
            variant: 'success',
            solid: true,
            toaster: 'b-toaster-top-center',
            autoHideDelay: 3000
          });
          return;
        }
        const referenceTime = report.report_date ? new Date(report.report_date).getTime() / 1000 : Date.now() / 1000;
        const updateItem = item => {
          if (item.report_id !== reportId) {
            return item;
          }
          const source = {
            ...item,
            ...report
          };
          return {
            ...source,
            ...this.processSingleReport(source, referenceTime)
          };
        };
        this.items = this.items.map(updateItem);
        this.originalItems = this.originalItems.map(updateItem);
        this.$bvToast.toast('Report refreshed successfully!', {
          title: 'Success',
          variant: 'success',
          solid: true,
          toaster: 'b-toaster-top-center',
          autoHideDelay: 3000
        });
      } catch (error) {
        console.error('Failed to refresh report:', error);
        this.$bvToast.toast('Failed to refresh report. Please try again.', {
          title: 'Error',
          variant: 'danger',
          toaster: 'b-toaster-top-center',
          solid: true
        });
      } finally {
        this.$set(this.fetchingUpdate, reportId, false);
      }
    }
  }
};